import React, { useState, useEffect, useCallback, useRef } from 'react';

export const errMsg = (err, fallback = 'Something went wrong') =>
  err?.response?.data?.error?.message || fallback;

// Loads a server-paged, server-sorted admin list. `fetcher(params)` resolves to an array carrying
// a `meta` property ({ page, limit, total }) — see the response interceptor in services/api.js.
// Changing a filter, the sort, or the page size returns to page 1.
// `initial` ({ page, limit, sort }) is where the list starts, for a screen that keeps its place in the address.
export function usePaged(fetcher, filters, defaultSort = { key: '', dir: 'desc' }, initial = {}) {
  const [page, setPage] = useState(initial.page || 1);
  const [limit, setLimit] = useState(initial.limit || 10);
  const [sort, setSort] = useState(initial.sort || defaultSort);
  const started = useRef(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const filterKey = JSON.stringify(filters);

  // Clicking the active column flips direction; clicking another column starts ascending.
  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetcher({ ...filters, page, limit, sort: sort.key || undefined, order: sort.key ? sort.dir : undefined });
      setRows(data);
      setTotal(data.meta?.total ?? data.length);
    } catch (err) {
      setError(errMsg(err, 'Failed to load'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher, filterKey, page, limit, sort]);

  useEffect(() => {
    if (!started.current) { started.current = true; return; } // the first run is the starting state, not a change
    setPage(1);
  }, [filterKey, limit, sort]);
  useEffect(() => { load(); }, [load]);

  return { rows, total, page, setPage, limit, setLimit, sort, setSort, toggleSort, loading, error, reload: load };
}

/**
 * Client-side twin of usePaged for short lists that are fetched whole (categories, staff):
 * search, filter, sort and paginate in memory, exposing the same interface so <DataTable> works
 * unchanged. `sorters` maps a column key to a value getter. With no default sort, the third click
 * on a column clears the sort and goes back to the natural order (e.g. the category tree).
 */
export function useClientTable(items, { q = '', match, predicate, filterKey = '', sorters = {}, defaultSort = { key: '', dir: 'asc' } }) {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sort, setSort] = useState(defaultSort);

  const toggleSort = (key) =>
    setSort((s) => {
      if (s.key !== key) return { key, dir: 'asc' };
      if (s.dir === 'asc') return { key, dir: 'desc' };
      return defaultSort.key === '' ? defaultSort : { key, dir: 'asc' };
    });

  const query = q.trim().toLowerCase();
  const processed = React.useMemo(() => {
    let rows = items;
    if (predicate) rows = rows.filter(predicate);
    if (query && match) rows = rows.filter((r) => match(r, query));
    const get = sorters[sort.key];
    if (get) {
      rows = [...rows].sort((a, b) => {
        const x = get(a);
        const y = get(b);
        const c = typeof x === 'string' ? x.localeCompare(y) : x - y;
        return sort.dir === 'asc' ? c : -c;
      });
    }
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, query, filterKey, sort]);

  useEffect(() => { setPage(1); }, [query, filterKey, sort, limit]);

  const pages = Math.max(1, Math.ceil(processed.length / limit));
  const safePage = Math.min(page, pages);
  const rows = processed.slice((safePage - 1) * limit, safePage * limit);

  return { rows, total: processed.length, page: safePage, setPage, limit, setLimit, sort, setSort, toggleSort, isCustomOrder: sort.key !== '' || query !== '' };
}

// Small outline icon for buttons; inherits the button's text colour.
export function PlusIcon() {
  return (
    <svg className="adm-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

export function Badge({ tone = 'grey', children }) {
  return <span className={`adm-badge adm-${tone}`}>{children}</span>;
}

// Segmented filter, as used above the tables in the Figma (All / Live / Draft / Ended).
export function Tabs({ options, value, onChange }) {
  return (
    <div className="adm-tabs" role="tablist">
      {options.map((o) => (
        <button key={o.value} role="tab" aria-selected={o.value === value} className={o.value === value ? 'on' : ''} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function PageHead({ title, subtitle, actions }) {
  return (
    <div className="adm-pagehead">
      <div>
        <h2>{title}</h2>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {actions && <div className="adm-pageactions">{actions}</div>}
    </div>
  );
}

export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '');
export const fmtDateTime = (d) => (d ? new Date(d).toLocaleString() : '');
